import React, { useState } from 'react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'

import chartColors from 'assets/styles/scss/shared/_chart-colors.module.scss'
import { LegendCustomFilter } from 'components/Shared/Charts/utils/LegendCustomFilter'
import { ChartTooltip } from 'components/Shared/Charts/ChartTooltip'
import { trackActions, capitalize } from 'containers/Helper'
import { isMobileView } from 'lib/GlobalConstants'
import { formatNumbers } from 'lib/Helper'

type StackedAreaChartProps = {
  data: any[]
  keys: any[]
  colors?: string[]
  xAxisDataKey: string
  title: string
  trackActionDetails?: { [key: string]: string }
}

const getDefaultColors = () => {
  const { iris, flame, seaGreen, blue } = chartColors

  return [iris, flame, seaGreen, blue]
}

/**
 * To override the default colors make sure
 * colors and keys are and same in length.
 */

const customTooltip = (
  props: any,
  tooltipTitle: string,
  legendFilterProps: { [key: string]: string },
) => {
  const filterColors = getDefaultColors()

  //tooltip data from recharts is in payload array, and its always the first object
  const tooltipData = props?.payload[0]
  if (tooltipData) {
    const { metric: date } = tooltipData.payload

    const contentobj: { [key: string]: string } = {}

    Object.entries(legendFilterProps).forEach(([key], index) => {
      !legendFilterProps[key]
        ? (contentobj[key] = formatNumbers(tooltipData.payload[key]))
        : delete filterColors[index]
    })

    return (
      <ChartTooltip
        title={tooltipTitle}
        content={contentobj}
        xAxisLabel={date}
        shouldTranslate={true}
        dotColor={filterColors.filter(Boolean)}
      />
    )
  }

  return null
}

const StackedAreaChart = ({
  data,
  keys,
  colors,
  xAxisDataKey,
  title,
  trackActionDetails = {},
}: StackedAreaChartProps) => {
  const defaultColors = colors || getDefaultColors()

  const [legendFilterProps, setLegendFilterProps] = useState(
    keys.reduce(
      (obj, item) => ({
        ...obj,
        [item.key]: false,
      }),
      {},
    ),
  )

  const checkIfLastFilter = (id: number) => {
    const legendValues = Object.values(legendFilterProps)
    if (legendValues.filter((legend) => !legend).length === 1) {
      return false
    }
    return !legendFilterProps[id]
  }

  const handleFilterClick = (e: any) => {
    const id = e.target.parentNode.id
    const { eventName, featureName, sectionName, widgetName, widgetType } =
      trackActionDetails
    const checkLastFilterValue = id && checkIfLastFilter(id)
    const toggleValue = checkLastFilterValue ? 'off' : 'on'
    if (!id) return
    // set key to true on filter click
    !!Object.keys(trackActionDetails).length &&
      trackActions(`${eventName}`, {
        featureName,
        sectionName,
        widgetName,
        widgetType,
        toggleName: capitalize(id),
        toggleValue,
      })

    setLegendFilterProps({
      ...legendFilterProps,
      [id]: checkLastFilterValue,
    })
  }

  const legendCustomFilter = ({ payload }: any) => {
    return (
      <LegendCustomFilter
        keys={keys}
        payload={payload}
        handleFilterClick={handleFilterClick}
        legendFilterProps={legendFilterProps}
      />
    )
  }

  return (
    <ResponsiveContainer height={isMobileView ? 270 : 290}>
      <AreaChart
        data={data}
        margin={{
          top: 5,
          right: 16,
          left: 0,
          bottom: 0,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={chartColors.gray} />
        <XAxis
          dataKey={xAxisDataKey}
          tick={{ fill: chartColors.black, fontSize: 12 }}
          tickMargin={10}
          tickLine={false}
          axisLine={false}
          label={{ position: 'insideBottomRight', offset: 30 }}
        />
        <YAxis
          tick={{ fill: chartColors.black, fontSize: 12 }}
          tickFormatter={(tick) => formatNumbers(tick)}
          tickMargin={8}
          width={50}
          tickLine={false}
          axisLine={false}
          dy={-5}
        />
        <Legend
          iconSize={0}
          cursor={'pointer'}
          wrapperStyle={{ top: isMobileView ? 190 : 280 }}
          content={legendCustomFilter}
          payload={defaultColors.map((pair: string, index: number) => ({
            color: pair,
            value: index,
          }))}
        />
        <Tooltip
          content={(props) => customTooltip(props, title, legendFilterProps)}
        />
        {keys.map((chart: { key: string }, index: number) => {
          const { key } = chart
          return (
            <Area
              type="monotone"
              dataKey={key}
              stroke={defaultColors[index]}
              strokeWidth={2}
              fill={defaultColors[index]}
              key={key}
              dot={false}
              activeDot={{ r: 6 }}
              fillOpacity={0.1}
              hide={legendFilterProps[key] === true}
            />
          )
        })}
      </AreaChart>
    </ResponsiveContainer>
  )
}

export default StackedAreaChart
